import React, { Component } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import Radium from "higher-order-components/configured-radium/"
import { Ribbon } from "containers"
import { InteriorSwatch } from "components"
import { IS_IOS, IS_IE, IS_EDGE } from "AtomicUtils/environment"
import { invokeFuncs } from "utils/function"
import { A, H5, Space, Tag, StarIcon } from "AtomicComponents"
import { Disclaimer } from "AtomicContainers"
import typographyStyles from "AtomicComponents/Typography/Typography.less"

const Price = ({ value, disclaimer, nameplate, storeTypeCategory, grade }) => (
  <div data-test={"price"} style={{ whiteSpace: "noWrap" }}>
    <A
      withoutStyle
      style={{ fontSize: "26px" }}
      onClick={(event) => {
        if (event && event.target && event.target.querySelector('button')) {
          event.target.querySelector('button').click()
        }
      }}
    >
      ${!!value && value.toLocaleString()}
      <Disclaimer
        Icon={StarIcon}
        gtm={{
          category: "Outlet Store",
          action: `Search Result - "${nameplate.toUpperCase()}", "${storeTypeCategory.toUpperCase()}" "${grade.toUpperCase()}" - Clicked "PRICE DISCLAIMER"`
        }}>
        {disclaimer}
      </Disclaimer>
    </A>
  </div>
)

Price.propTypes = {
  gtmEvents: PropTypes.array,
  disclaimer: PropTypes.string,
  value: PropTypes.number
}

const TileExtraInfoText = ({ textArray = [] }) => (
  <div
    style={{
      marginLeft: "1em",
      textAlign: "right"
    }}>
    {textArray.map((text, key) =>
        !!text && (
          <p
            data-test="extra-info-text"
            key={key}
            style={{
              margin: 0,
              fontSize: "90%",
              color: "#7F7F7F",
              fontWeight: "200"
            }}>
            {text}
          </p>
        ))}
  </div>
)

TileExtraInfoText.propTypes = {
  textArray: PropTypes.array
}

const FeaturedRibbon = () => <Ribbon text={"Featured"} backgroundColor={"#910A2D"} />

// Each type of vehicle has different rule for title
const conditionalTitle = (nameplate, bodyStyle, grade) => {
  if (
    (nameplate && nameplate.toLowerCase().includes("cx")) ||
    (bodyStyle && bodyStyle.toLowerCase().includes("rf"))
  ) {
    return `${grade}`
  }
  return `${bodyStyle} ${grade}`
}

class StockTile extends Component {
  handleOnClick = (e) => {
    const { gtmEvents } = this.props
    gtmEvents.click && invokeFuncs(gtmEvents.click)
  }
  render() {
    const {
      nameplate,
      storeType,
      storeTypeCategory,
      image,
      grade,
      generation,
      engineSize,
      bodyStyle,
      backgroundImage,
      featured,
      fuelType,
      interiorSwatchImage,
      interiorTitle,
      transmissionType,
      gtmEvents,
      price,
      href,
      disclaimer,
      odometer,
      driveTrain,
      extraInfo,
      dealerState
    } = this.props
    return (
      <div
        data-test={"stock-tile"}
        style={{
          backgroundColor: "#fff",
          border: "1px solid #D5D5D5",
          transition: "box-shadow .2s",
          display: "flex",
          height: "100%",
          flexDirection: "column",
          justifyContent: "space-between",
          width: "100%",
          ":hover": {
            boxShadow: "0px 5px 10px #D5D5D5"
          }
        }}>
        <a
          href={href}
          onTouchEnd={e => IS_IOS && this.handleOnClick(e)}
          onClick={e => !IS_IOS && this.handleOnClick(e)}
          className={classnames({
            [typographyStyles.withoutStyle]: true
          })}
          style={{
            color: "#101010",
            textDecoration: "none",
            fontWeight: "initial",
            width: "100%",
            height: (IS_IE || IS_EDGE) && "100%",
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column"
          }}>
          <div
            style={[
              backgroundImage && {
                backgroundImage: `url(${backgroundImage})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center bottom",
                flex: "0 0 auto"
              }
            ]}>
            {featured && <FeaturedRibbon />}
            {image ? ( // If there's no image, add a place holder svg
              <img style={{ width: "100%", paddingTop: "2.5em" }} src={image} />
            ) : (
              <svg style={{ width: "100%", paddingTop: "2.5em", opacity: 0 }} viewBox="0 0 980 520">
                <rect x="10" y="10" width="980" height="520" />
              </svg>
            )}
          </div>
          <div
            style={{
              padding: "1em 2em",
              flex: "1 1 auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between"
            }}>
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}>
              <Tag color={storeType === 0 ? 'orange' : 'blue'}>{storeTypeCategory || (storeType === 1 ? 'Clearance' : 'Demo')}</Tag>
              <Space />
              <H5 withDetails>
                {conditionalTitle(nameplate, bodyStyle, grade)}
              </H5>
              <p
                style={[
                  stockTileStyles.modelDescription,
                  { marginTop: "0.4em" }
                ]}>
                {engineSize} {fuelType} {driveTrain} {transmissionType}{" "}
                {odometer > 0 && " |  " + odometer.toLocaleString('nz') + "km"}
              </p>
              {generation === "Previous" && (
                <p style={[stockTileStyles.modelDescription, { marginBottom: 0 }]}>
                  Previous Model
                </p>
              )}
            </div>
          </div>
        </a>
        <div style={{ padding: "0em 2em 1em" }}>
          <div
            style={{
              borderTop: "1px solid #E5E5E5",
              paddingTop: "1.2em",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              textAlign: "left",
              minHeight: "2em" // match the swatch height so there's no mismatch if swatch not available
            }}>
            <div>
              <div
                style={{
                  textTransform: "uppercase",
                  whiteSpace: "noWrap",
                  fontWeight: 600,
                  letterSpacing: "0.15em",
                  fontSize: "80%",
                  marginBottom: ".5em"
                }}>
                Price in {dealerState}
              </div>
              <div style={stockTileStyles.price}>
                <Price value={price} disclaimer={disclaimer} nameplate={nameplate} storeTypeCategory={storeTypeCategory} grade={grade} />
              </div>
            </div>
            <div style={{ flex: "1 1 100%" }}>
              {extraInfo && <TileExtraInfoText textArray={extraInfo} />}
            </div>
            {!!interiorSwatchImage && (
              <InteriorSwatch label={interiorTitle} imageSrc={interiorSwatchImage} />
            )}
          </div>
        </div>
      </div>
    )
  }
}

StockTile.propTypes = {
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  faded: PropTypes.bool,
  lazyLoadImages: PropTypes.bool,
  href: PropTypes.string,
  backgroundImage: PropTypes.string,
  featured: PropTypes.bool,
  interiorSwatchImage: PropTypes.string,
  interiorTitle: PropTypes.string,
  accessories: PropTypes.array,
  extraInfo: PropTypes.array,
  gtmEvents: PropTypes.shape({
    click: PropTypes.array,
    disclaimer: PropTypes.array
  }),
  nameplate: PropTypes.string,
  storeType: PropTypes.number.isRequired,
  storeTypeCategory: PropTypes.string,
  engineSize: PropTypes.string,
  image: PropTypes.string,
  grade: PropTypes.string,
  generation: PropTypes.string,
  bodyStyle: PropTypes.string,
  fuelType: PropTypes.string,
  transmissionType: PropTypes.string,
  price: PropTypes.number,
  disclaimer: PropTypes.string,
  odometer: PropTypes.number,
  driveTrain: PropTypes.string,
  dealerState: PropTypes.string
}

const stockTileStyles = {
  price: {
    fontSize: "1.6em",
    fontWeight: "300"
  },
  modelDescription: {
    fontWeight: 200,
    lineHeight: "1.5em",
    marginTop: 0,
    marginLeft: 0,
    marginRight: 0,
    marginBottom: 0
  }
}

export default Radium(StockTile)



// WEBPACK FOOTER //
// ./src/containers/outlet-store/stock-tile.jsx